import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { showErrorToast, tagsEnabled } from '@Helpers';

import { ModalComponent, ModalLoading, ModalSuccess } from '@Components';
import { Combobox, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList } from '@Components/ui-v2/combobox';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFetchData } from '@CustomHooks';
import { Options, UserType } from '@Models';
import { bopsApi } from '@Network';
import { AppDispatch, tableServerRevision } from '@Store';
import config from '../../config';
import MultiSelect from '../ui/multiselect';
import Spinner from '../ui/spinner';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: Options[];
  setloading: React.Dispatch<React.SetStateAction<boolean>>;
  setUsersDisplay: React.Dispatch<React.SetStateAction<UserType[]>>;
  fetchUsers: () => void;
  IncrementEvent: (event: string) => void;
  usersDisplay: UserType[];
  clientValue: Options | undefined;
}

interface FormFields {
  email: string;
  client: string;
  username?: string;
  password: string;
}

const UserAddModal = ({
  isOpen,
  onClose,
  clients,
  fetchUsers,
  setloading,
  clientValue,
  IncrementEvent,
}: UserModalProps) => {
  const { t } = useTranslation();

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [emailCreated, setEmail] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormFields>();

  const emailValue = watch('email');
  const clientSelected = watch('client');

  const {
    data: clientTags,
    error: errorClientTags,
    isLoading: clientTagsLoading,
    refetch: refetchClientTags,
  } = useFetchData(bopsApi.getClientTags, null, true);

  useEffect(() => {
    // Skipped where tags are disabled: the field is hidden, so there is
    // nothing to populate and no reason to hit the endpoint.
    if (clientSelected && tagsEnabled) {
      setSelectedTags([]);
      refetchClientTags(clientSelected);
    }
  }, [clientSelected]);

  useEffect(() => {
    reset();
    setSelectedTags([]);
  }, [isOpen]);

  const onSubmit = (data: FormFields) => {
    const { client, email, username, password } = data;
    setOpenLoading(true);
    setloading(true);
    setEmail(email);

    bopsApi
      .postUser({
        email,
        client,
        username,
        password,
        tags: selectedTags,
      })
      .then(() => {
        onClose();
        setOpenLoading(false);
        setOpenSuccess(true);
        setTimeout(() => {
          IncrementEvent('usersEvent');
          fetchUsers();
        }, 3000);
        setloading(false);
      })
      .catch((error) => {
        setOpenLoading(false);
        setloading(false);
        let errorMessage = t('User.ErrorCreate');
        if (error.response?.data?.detail === 'User already exists') {
          errorMessage = t('User.ErrorAlreadyExists');
        }

        if (error.response?.data?.detail === 'Invalid username') {
          errorMessage = t('User.ErrorInvalidUsername');
        }

        if (error.response?.data?.detail === 'Username already exists') {
          errorMessage = t('User.ErrorUsernameTaken');
        }

        showErrorToast(errorMessage);
      });
  };

  return (
    <>
      <ModalComponent isOpen={isOpen} onClose={onClose} title={t('User.TitleModal')} closeClickOutside={false}>
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="pr-[20px]">
              <span className="text-gray-900 text-sm" id="email">
                {`${t('User.Email')}*`}
              </span>
              <Input
                type="text"
                id="email"
                className={`${errors?.email ? 'border-red-500 focus:border-red-500' : 'focus:border-gray-600'} mb-2`}
                {...register('email', {
                  required: `${t('User.Email')} ${t('Global.Required')}`,
                  validate: {
                    matchPattern: (v) => /^\S+@\S+\.\S+$/.test(v) || t('User.ErrorEmail'),
                  },
                })}
              />
            </div>
            {errors?.email && <p className="text-sm text-red-500">{errors?.email?.message}</p>}
            {config.isGs1Flavor && (
              <>
                <div className="pr-[20px]">
                  <span className="text-gray-900 text-sm" id="username">
                    {`${t('User.Username')}*`}
                  </span>
                  <Input
                    type="text"
                    id="username"
                    className={`${
                      errors?.username ? 'border-red-500 focus:border-red-500' : 'focus:border-gray-600'
                    } mb-2`}
                    {...register('username', {
                      required: `${t('User.Username')} ${t('Global.Required')}`,
                      validate: {
                        matchPattern: (v) =>
                          (v ? /^[a-zA-Z0-9_+\-.#!$'`~@^]+$/.test(v) : false) || t('User.ErrorUsername'),
                        notSameEmail: (v) => v !== emailValue || t('User.UsernameNotEmail'),
                        notEmail: (v) => (v ? !/^\S+@\S+\.\S+$/.test(v) : false) || t('User.UsernameCannotBeEmail'),
                        length: (v) => (v && v.length >= 6 && v.length <= 30) || t('User.UsernameLengthError'),
                      },
                    })}
                  />
                </div>
                {errors?.username && <p className="text-sm text-red-500">{errors?.username?.message}</p>}
              </>
            )}
            {config.isLogycaFlavor && (
              <>
                <div className="pr-[20px] mt-4">
                  <span className="text-gray-900 text-sm" id="password">
                    {`${t('User.Password')}*`}
                  </span>
                  <Input
                    type="password"
                    id="password"
                    className={`${
                      errors?.password ? 'border-red-500 focus:border-red-500' : 'focus:border-gray-600'
                    } mb-2`}
                    {...register('password', {
                      required: `${t('User.Password')} ${t('Global.Required')}`,
                      validate: {
                        length: (v) => (v && v.length >= 12) || t('User.PasswordLengthError'),
                        hasUppercase: (v) => (v && /[A-Z]/.test(v)) || t('User.PasswordUppercaseError'),
                        hasLowercase: (v) => (v && /[a-z]/.test(v)) || t('User.PasswordLowercaseError'),
                        hasNumber: (v) => (v && /\d/.test(v)) || t('User.PasswordNumberError'),
                      },
                    })}
                  />
                </div>
                {errors?.password && <p className="text-sm text-red-500">{errors?.password?.message}</p>}
              </>
            )}
            <div className="text-sm mt-4">
              <Controller
                control={control}
                rules={{ required: true }}
                defaultValue={clientValue?.value}
                name="client"
                render={({ field: { onChange } }) => (
                  <Combobox
                    items={clients}
                    itemToStringLabel={(item: Options) => item.label}
                    value={clients.find((c) => c.value === clientSelected) ?? null}
                    onValueChange={(item: Options | null) => {
                      onChange(item?.value || '');
                    }}>
                    <ComboboxInput placeholder={t('User.Select')} />
                    <ComboboxContent>
                      <ComboboxList>
                        {(item: Options) => (
                          <ComboboxItem key={item.value} value={item}>
                            {item.label}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                )}
              />
              {errors.client && (
                <p className="text-sm text-red-500">{`${t('User.Client')} ${t('Global.Required')}`} </p>
              )}
            </div>
            {tagsEnabled && clientSelected && (
              <div className="text-sm mt-4 mb-10">
                {clientTags ? (
                  <MultiSelect
                    placeholder={t('User.EditTagsPlaceholder')}
                    emptyText={t('User.EditTagsEmpty')}
                    options={
                      clientTags
                        ? clientTags
                            .filter((tag) => !selectedTags.includes(tag))
                            .map((tag) => ({ label: tag, value: tag }))
                        : []
                    }
                    selected={selectedTags.map((tag) => ({ label: tag, value: tag }))}
                    onChange={(selected) => {
                      setSelectedTags(selected.map((option) => option.value));
                    }}
                  />
                ) : clientTagsLoading ? (
                  <Spinner />
                ) : (
                  errorClientTags && <p className="text-sm text-red-500">{`${t('User.EditTagsLoadingError')}`} </p>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-center mt-4 w-full">
              <TrackedButton
                variant="outline"
                buttonName="ADD_USER_CANCELED"
                onClick={() => {
                  onClose();
                }}
                className="w-full bg-white"
                isSubmit={false}>
                {t('Global.Canceled')}
              </TrackedButton>
              <TrackedButton buttonName="USER_ADDED" isSubmit className="w-full">
                <div className="text-white"> {t('User.Add')} </div>
              </TrackedButton>
            </div>
          </form>
        </div>
      </ModalComponent>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center">
          <span className="font-semibold text-[18px] text-center"> {emailCreated}</span>
          <span className="text-[18px]"> {t('User.AddSuccess')} </span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center -mt-[9px]">
          <span className="font-semibold text-center"> {emailCreated}</span> {t('User.LoadingCreate')}
        </div>
      </ModalLoading>
    </>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { IncrementEvent } = tableServerRevision;

  return {
    IncrementEvent: (event: string) => dispatch(IncrementEvent(event)),
  };
};

export default connect(null, mapDispatchToProps)(UserAddModal);
